<template>
  <Card class="pa-2 subheading" :style="{ height: cardHeight }">
    <div id="info-header">
      <span class="subheading">{{ $t('Inverter') }}</span> {{ $t('Live Data') }}
    </div>
    <DataTable class="inv-data-card" :headers="computedInvHeaders" :items="invData"
      :style="{ height: tableHeight }"
      :isPaginationFalse="true">
      <template v-slot:firstCol="{ text }">
        <td>
          <v-layout row wrap align-center class="first-col-width">
            <div class="text__width">
              {{ text.name }}
            </div>
            <div>
              <v-icon
                :color="text.ac_power == undefined ? 'grey' : ( text.ac_power <= 0 ? 'red' : 'green' )"
                class="icon-class" size="10">
                circle</v-icon>
            </div>
          </v-layout>
        </td>
      </template>
    </DataTable>
  </Card>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import LiveDb from '@/services/LiveDb.js'
import { mapGettersMixin } from '@/util/mixin/index'

export default {
  mixins: [ mapGettersMixin ],
  props: {
    parameter: {
      type: Array,
      default: function() {
        return []
      }
    },
    parameterNameMap: {
      type: Object,
      default: function() {
        return {}
      }
    },
    nameMap: {
      type: Object,
      default: function() {
        return {}
      }
    },
    cardHeight: {
      type: String,
      default: 'calc(100vh - 570px)'
    },
    tableHeight: {
      type: String,
      default: 'calc(100vh - 623px)'
    }
  },
  data() {
    return {
      invHeaders: [],
      invData: [],
      liveInverterData: {},
      computedInverterData: [],
      client: null
    }
  },
  computed: {
    ...mapGetters([ 'getSelectedPlantId' ]),
    computedInvHeaders() {
      const cloneHeader = _.cloneDeep(this.invHeaders)
      return cloneHeader.map(el => {
        el['text'] = this.$t(el.text)
        return el
      })
    }
  },
  async mounted() {
    this.initialStateValues()
    await this.liveInvData()
    this.setIntervalWithoutDelay(await this.getInvGeneration, 60000)
    this.mqttHandler()
  },
  beforeDestroy() {
    this.$mqtt.unsubscribe(`live/${this.getSelectedPlantId}/inverter`)
  },
  methods: {
    initialStateValues() {
      this.initialHeader()
      this.invData = []
      this.liveInverterData = {}
      this.computedInverterData = []
    },
    isFloat(value) {
      if (
        typeof value === 'number' &&
        !Number.isNaN(value) &&
        !Number.isInteger(value)
      ) {
        return true;
      }

      return false;
    },
    toThreeDecimalPlaces(props) {
      if (props !== undefined && props !== null) {
        if (this.isFloat(Number(props))) {
          return props == 0 ? 0 : Number.parseFloat(props).toFixed(3)
        } else {
          return props
        }
      } else {
        return 'No Data'
      }
    },
    async getInvGeneration() {
      await this.computedInvData()

      const computedMap = this.computedInverterData.reduce((acc, curr) => {
        acc[curr.id] = curr
        return acc
      }, {})

      if (this.invData.length === 0) {
        const dataFromMqtt = Object.keys(this.parameterNameMap).map(idConfigured => {
          const newDataObj = {
            id: idConfigured,
            name: this.nameMap[idConfigured]
          }

          if (this.liveInverterData[idConfigured]) {
            for (const [key, value] of Object.entries(this.liveInverterData[idConfigured])) {
              if (key !== 'id' && key !== 'name') {
                newDataObj[key] = this.toThreeDecimalPlaces(value)
              }
            }
          }

          const computed = computedMap[idConfigured]
          if (computed) {
            newDataObj.maxMinusMinEnergy = this.toThreeDecimalPlaces(computed.maxMinusMinEnergy)
          }

          return newDataObj
        })
        
        this.invData = dataFromMqtt
        this.mapHeaderHandler()
      } else {
        this.invData.forEach(row => {
          const computed = computedMap[row.id]
          if (computed) {
            this.$set(row, 'maxMinusMinEnergy', this.toThreeDecimalPlaces(computed.maxMinusMinEnergy))
          }
        })
      }
    },
    async liveInvData() {
      let invParamJson = {}
      this.parameter.forEach(key => {
        invParamJson[key] = key
      })
      const invParam = Buffer.from(JSON.stringify(invParamJson)).toString('base64')
      // Live data query
      const response = await LiveDb.getLiveDeviceData(this.getSelectedPlantId, 'inverter', invParam)
      this.liveInverterData = response.data.message
    },
    async computedInvData() {
      const inverterParams = Buffer.from(JSON.stringify({
        'energy': 'maxMinusMinEnergy'
      })).toString('base64')

      // Computed data query
      const response = await LiveDb.getGenerationData(this.getSelectedPlantId, 'inverter', inverterParams)
      this.computedInverterData = response.data.message
    },
    mapHeaderHandler() {
      const header = []
      this.parameter.forEach(param => {
        const ob = {}
        const name = this.parameterNameMap[this.invData[0].id][0].config[param].name
        const unit = this.parameterNameMap[this.invData[0].id][0].config[param].unit ? this.parameterNameMap[this.invData[0].id][0].config[param].unit : ''
        ob['text'] = `${name} ${unit ? `(${unit})` : ''}`
        ob['value'] = param
        ob['align'] = 'center'
        header.push(ob)
      })
      this.initialHeader()
      this.invHeaders = [...this.invHeaders, ...header]
    },
    mqttHandler() {
      this.$mqtt.subscribe(`live/${this.getSelectedPlantId}/inverter`)
      if (this.mqttBound) return;
      this.mqttBound = true;
      this.$mqtt.on('message', (topic, message) => {
        if (topic.startsWith('live/')) {
          if (topic.endsWith('inverter')) {
            const mqttData = JSON.parse(message.toString()).deviceList[0]
            if (mqttData.ignore_status !== 1) {
              const index = this.invData.findIndex(obj => obj.id === mqttData.device_id)
              this.parameter.forEach(key => {
                this.invData[index][key] = this.toThreeDecimalPlaces(mqttData[key])
              })
            }
          }
        }
      })
      this.$mqtt.on('error', function(error) {
        console.log('ERROR: ', error)
      })

      this.$mqtt.on('offline', function() {
        console.log('offline')
      })

      this.$mqtt.on('reconnect', function() {
        console.log('reconnect')
      })
    },
    initialHeader() {
      this.invHeaders = [{
        text: 'INV Name',
        align: 'center',
        sortable: false,
        value: 'name',
        width: '20%'
      },
      {
        text: 'Generation (kWh)',
        align: 'center',
        value: 'maxMinusMinEnergy',
        sortable: false
      }
      ]
    }
  },
  watch: {
    async getSelectedPlantId(newVal, oldVal) {
      this.initialStateValues()
      this.$mqtt.unsubscribe(`live/${oldVal}/inverter`)
      await this.liveInvData()
      this.setIntervalWithoutDelay(await this.getInvGeneration, 60000)
      this.mqttHandler()
    }
  }
}
</script>

<style scoped>
.icon-class {
  margin-bottom: 2px;
  margin-left: 12px;
}

.first-col-width {
  width: 150px;
  word-wrap: break-word;
}

#info-header span {
  border-bottom: 2px solid #417CDE;
  display: inline-block;
  vertical-align: top;
}

#info-header {
  padding-bottom: 16px;
}

.text__width {
  width: 120px;
}

</style>
<style>
.inv-data-card .v-table__overflow .v-table thead tr:first-child {
  position: sticky;
  top: 0px;
  z-index: 2;
  background: var(--v-primary-base);
  border-top: 1px solid #38405C;
}

.inv-data-card .v-table__overflow .v-table tbody td:first-child,
table.v-table thead th:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
  background: var(--v-primary-base);
  border-right: 1px solid #38405C;
}

</style>
